import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
} from "@mui/material";
import { useEffect, useState } from "react";
import { CommoditySelector } from "../components/CommoditySelector";

interface ChargeLayerModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (commodityId: number | null, weight?: number) => void;
  isLoading: boolean;
  title: string;
  chargeId: number | null;
}

export const ChargeLayerModal = ({
  open,
  onClose,
  onSave,
  isLoading,
  title,
}: ChargeLayerModalProps) => {
  const [commodityId, setCommodityId] = useState<number | null>(null);
  const [showError, setShowError] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setCommodityId(null);
      setShowError(false);
    }
  }, [open]);

  const handleSave = () => {
    if (!commodityId) {
      setShowError(true);
      return;
    }

    onSave(commodityId);
    // Modal is closed by the parent via onSuccess callback
  };

  const handleCommodityChange = (id: number | null) => {
    setCommodityId(id);
    if (id) setShowError(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: "20%",
            maxWidth: "700px",
            borderRadius: "12px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          color: "white",
          padding: (theme) => theme.spacing(2),
          "& h2": {
            fontSize: "1.5rem",
            fontWeight: 600,
          },
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent
        sx={{
          padding: (theme) => theme.spacing(3),
          display: "flex",
          flexDirection: "column",
          gap: (theme) => theme.spacing(3),
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box>
            <CommoditySelector
              value={commodityId}
              onChange={handleCommodityChange}
            />
            {showError && (
              <FormHelperText
                sx={{
                  color: (theme) => theme.palette.error.main,
                  marginTop: (theme) => theme.spacing(1),
                }}
              >
                Commodity selection is required
              </FormHelperText>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: (theme) => theme.spacing(2, 3, 3),
          width: "100%",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              onClick={onClose}
              disabled={isLoading}
              variant="contained"
              sx={{
                padding: (theme) => theme.spacing(1, 3),
                borderRadius: "4px",
                width: "100%",
                backgroundColor: (theme) =>
                  theme.palette.charges.status.inactive,
                color: "white",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.grey[600],
                },
              }}
            >
              CANCEL
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              onClick={handleSave}
              disabled={isLoading || !commodityId}
              variant="contained"
              sx={{
                padding: (theme) => theme.spacing(1, 3),
                borderRadius: "4px",
                width: "100%",
                backgroundColor: (theme) => theme.palette.primary.main,
                color: "white",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.primary.dark,
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "SAVE"
              )}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};
